import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { NumericFormat } from "react-number-format";
import { Button } from "../../../../../components/buttons/button";
import { ContainerCard } from "../../../../../components/cards/containerCard";
import { TextInput } from "../../../../../components/inputs/textInput";
import { TitleSubLoader } from "../../../../../components/loaders/titleSubLoader";
import { TitleSub } from "../../../../../components/typography/titleSub";
import {
  formatToNumber,
  parseStringToNumber,
} from "../../../../../helperFunctions";
import {
  useGetCooperativeDepositsConfigQuery,
  useUpdateDepositsConfigMutation,
} from "../../../../../redux/features/cooperative/cooperative.api";
import { depositConfigurationSchema } from "../../../../../validationSchema";

export const DepositConfigurations = () => {
  const [mode, setMode] = useState<"view" | "edit">("view");

  const {
    data: deposistsConfig,
    isLoading: fetchingConfig,
    refetch: refetchConfig,
  } = useGetCooperativeDepositsConfigQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const [updateDeposits, { isLoading: updatingDeposits }] =
    useUpdateDepositsConfigMutation();

  const {
    values,
    errors,
    touched,
    handleBlur,
    setFieldValue,
    handleSubmit,
    handleChange,
  } = useFormik({
    initialValues: {
      withdrawalDays: "",
      depositsInterestRate: "",
    },
    validationSchema: depositConfigurationSchema,
    onSubmit: async (data) => {
      const resp = await updateDeposits({
        InterestRate: parseStringToNumber(data.depositsInterestRate)!,
        NumberOfDaysToWithdrawal: parseStringToNumber(data.withdrawalDays)!,
      });

      if (resp.data?.success) {
        setMode("view");
        refetchConfig();
      }
    },
  });

  useEffect(() => {
    if (deposistsConfig?.data) {
      setFieldValue(
        "withdrawalDays",
        deposistsConfig.data.NumberOfDaysToWithdrawal
      );
      setFieldValue("depositsInterestRate", deposistsConfig.data.InterestRate);
    }
    // eslint-disable-next-line
  }, [deposistsConfig]);

  return (
    <ContainerCard className="!px-0">
      <div
        className="
        w-full flex flex-row gap-4
        items-center justify-between
        mb-6 px-4 md:px-8
        "
      >
        <p className="font-bold text-base">Deposits Settings</p>

        <Button
          text={mode === "edit" ? "Save" : "Edit"}
          onClick={() => {
            if (mode === "edit") {
              handleSubmit();
            } else {
              setMode("edit");
            }
          }}
          type="button"
          className="!min-w-[80px] !max-w-fit"
          size="small"
          loading={updatingDeposits}
          disabled={updatingDeposits}
          variation="primary"
        />
      </div>

      {/* Configuration Sub-body */}
      <div className="px-4 md:px-8 border-b py-3">
        {fetchingConfig ? (
          <TitleSubLoader />
        ) : (
          <>
            <TitleSub
              title="Withdrawal Approval"
              subtitle={"How long before withdrawal is approved?"}
              subtitleClassName="!mt-0"
            />

            <div className="mt-3">
              {mode === "view" && (
                <div className="text-sm md:text-base text-pry-green-200 font-medium">
                  {deposistsConfig?.data?.NumberOfDaysToWithdrawal
                    ? `${formatToNumber(
                        deposistsConfig?.data?.NumberOfDaysToWithdrawal
                      )} day(s)`
                    : "No duration set"}
                </div>
              )}

              {mode === "edit" && (
                <NumericFormat
                  suffix={undefined}
                  prefix={undefined}
                  decimalScale={0}
                  thousandSeparator
                  allowLeadingZeros={false}
                  customInput={TextInput}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="text"
                  name={"withdrawalDays"}
                  value={values.withdrawalDays}
                  placeholder="Input duration (in days)"
                  hasIcon={false}
                  iconSvg={undefined}
                  errors={
                    touched?.withdrawalDays && errors?.withdrawalDays
                      ? errors?.withdrawalDays
                      : ""
                  }
                  inputClass=""
                  className="!p-0 !pl-4"
                  autoComplete="off"
                  customIcon={
                    <p
                      className="
                  font-medium text-sm md:text-base
                  py-[14px] px-[16px] bg-white
                  border border-pry-dark-green-300/10
                  rounded-e-lg
                  "
                    >
                      Days
                    </p>
                  }
                  customIconPosition="right"
                />
              )}
            </div>
          </>
        )}
      </div>

      <div className="px-4 md:px-8 border-b-0 py-3">
        {fetchingConfig ? (
          <TitleSubLoader />
        ) : (
          <>
            <TitleSub
              title="Interest Rate"
              subtitle={"Amount of interest due per period, on a deposit."}
              subtitleClassName="!mt-0"
            />

            <div className="mt-3">
              {mode === "view" && (
                <div className="text-sm md:text-base text-pry-green-200 font-medium">
                  {deposistsConfig?.data?.InterestRate
                    ? `${formatToNumber(deposistsConfig?.data?.InterestRate, {
                        decimalPlaces: 2,
                        style: "percent",
                      })}`
                    : "No rate set"}
                </div>
              )}

              {mode === "edit" && (
                <NumericFormat
                  suffix={undefined}
                  prefix={undefined}
                  decimalScale={2}
                  thousandSeparator
                  allowLeadingZeros={false}
                  customInput={TextInput}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="text"
                  name={"depositsInterestRate"}
                  value={values.depositsInterestRate}
                  placeholder="Input interest rate"
                  hasIcon={false}
                  iconSvg={undefined}
                  errors={
                    touched?.depositsInterestRate &&
                    errors?.depositsInterestRate
                      ? errors?.depositsInterestRate
                      : ""
                  }
                  inputClass=""
                  className="!p-0 !pr-4"
                  autoComplete="off"
                  customIcon={
                    <div
                      className="
                  font-medium text-sm md:text-base
                  py-[14px] px-[16px] bg-white
                  border border-pry-dark-green-300/10
                  rounded-s-lg
                  "
                    >
                      %
                    </div>
                  }
                  customIconPosition="left"
                />
              )}
            </div>
          </>
        )}
      </div>
    </ContainerCard>
  );
};
